import { type ClassValue, clsx } from 'clsx';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import type { StoreApi, UseBoundStore } from 'zustand';

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

export const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(
  _store: S,
) => {
  const store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (const k of Object.keys(store.getState())) {
    (store.use as any)[k] = () => store((s) => s[k as keyof typeof s]);
  }

  return store;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDisplayDateOnly(
  date: Date | string | number,
  opts: Intl.DateTimeFormatOptions = {},
) {
  return new Intl.DateTimeFormat('it-IT', {
    month: opts.month ?? 'long',
    day: opts.day ?? 'numeric',
    year: opts.year ?? 'numeric',
    ...opts,
  }).format(new Date(date));
}

export function formatDateOnly(date: Date | string | number) {
  return format(date, 'yyyy-MM-dd');
}

export function toSentenceCase(str: string) {
  return str
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim();
}

export function preventNullString(value: string | undefined | null) {
  return value === 'null' ? '' : (value ?? '');
}

/**
 * Return a new object whose keys are ordered according to `order`.
 * Any keys in `obj` but _not_ in `order` will come last, in their original
 * insertion order.
 *
 * @param {Record<string, TItem>} obj      - the source object
 * @param {string[]} order  - desired key order (may include keys not in obj)
 * @returns {Record<string, TItem>}
 */
export function sortObjectByKeyOrder<TItem>(
  obj: Record<string, TItem>,
  order: string[],
): Record<string, TItem> {
  // build a map from key → its index in `order` (or Infinity if absent)
  const indexMap = new Map(order.map((key, idx) => [key, idx]));

  return Object.fromEntries(
    Object.entries(obj).sort(([keyA], [keyB]) => {
      const ia = indexMap.has(keyA) ? indexMap.get(keyA) : Infinity;
      const ib = indexMap.has(keyB) ? indexMap.get(keyB) : Infinity;
      return ia! - ib!;
    }),
  );
}
